import fs from "node:fs";
import http from "node:http";
import https from "node:https";
import path from "node:path";
import { downloadImage, getFilenameFromUrl } from "@iflyrpa/share";
import sizeOf from "image-size";
import { Http } from "../http";

const { getABogus } = require("./douyinSafe/a_bogus.js");

const { generateAuthorization, randomS, canonicalQueryString, calculateFileCrc32 } = require("./douyinSign.js");

// ==================== 类型定义 ====================
interface UploadAuth {
    status_code: number;
    auth: string;
    ak: string;
    status_msg?: string;
}

interface ImageXResponse {
    ResponseMetadata: {
        RequestId: string;
        Action?: string;
        Version?: string;
        Service?: string;
        Region?: string;
        Error?: {
            Code: string;
            Message: string;
        };
    };
    Result?: {
        RequestId: string;
        SessionKey: string;
        UploadAddress: {
            UploadHosts: string[];
            StoreInfos: Array<{
                StoreUri: string;
                Auth: string;
                SessionKey?: string;
            }>;
            UploadHeader?: Array<{
                Key: string;
                Value: string;
            }>;
        };
    };
}

interface UploadResult {
    uri: string;
    width: number;
    height: number;
    size: number;
    md5: string;
}

interface ImageInfo {
    localPath: string;
    width: number;
    height: number;
    size: number;
    md5: string;
}

// ==================== 抖音图片上传管理器 ====================
export class DouyinImageUploader {
    private proxyHttp: Http;
    private headers: Record<string, string>;
    private queryParams: Record<string, string>;
    private tmpCachePath: string;

    private authCache: UploadAuth | null = null;
    private authExpireTime: number = 0;
    private readonly AUTH_REFRESH_BUFFER = 5 * 60 * 1000;

    private readonly CONCURRENT_LIMIT = 10;
    private readonly UPLOAD_DELAY = 1000;
    private readonly UPLOAD_TIMEOUT = 300000;
    private readonly TOS_UPLOAD_TIMEOUT = 600000;

    private uploadAgent: https.Agent | http.Agent;

    constructor(
        proxyHttp: Http,
        headers: Record<string, string>,
        queryParams: Record<string, string>,
        tmpCachePath: string,
        isHttps: boolean = true,
    ) {
        this.proxyHttp = proxyHttp;
        this.headers = headers;
        this.queryParams = queryParams;
        this.tmpCachePath = tmpCachePath;

        const Agent = isHttps ? https.Agent : http.Agent;

        const agentOptions: https.AgentOptions = {
            keepAlive: true,
            maxSockets: this.CONCURRENT_LIMIT,
            maxFreeSockets: 2,
            timeout: this.TOS_UPLOAD_TIMEOUT,
        };

        (agentOptions as any).freeSocketTimeout = 30000;

        this.uploadAgent = new Agent(agentOptions);
    }

    /**
     * 获取有效的上传凭证（带缓存）
     */
    private async getValidAuth(): Promise<UploadAuth> {
        const now = Date.now();

        if (this.authCache && this.authExpireTime > now + this.AUTH_REFRESH_BUFFER) {
            console.log("使用缓存的STS凭证");
            return this.authCache;
        }

        console.log("申请新的STS凭证...");

        const queryString = new URLSearchParams(this.queryParams).toString();
        const aBogus = getABogus(
            `https://creator.douyin.com/web/api/media/upload/auth/v5/?${queryString}`,
            {},
            this.headers["user-agent"],
        );
        this.queryParams.a_bogus = aBogus;
        const newQueryString = new URLSearchParams(this.queryParams).toString();
        const uploadAuth = await this.withRetry(
            () =>
                this.proxyHttp.api<UploadAuth>(
                    {
                        method: "get",
                        url: `https://creator.douyin.com/web/api/media/upload/auth/v5/?${newQueryString}`,
                        headers: {
                            ...this.headers,
                            "Content-Type": "application/json",
                        },
                    },
                    { timeout: 5 * 60 * 1000, retries: 0 },
                ),
            3,
            1000,
            "获取上传凭证",
        );

        if (!uploadAuth.auth) {
            throw new Error(uploadAuth.status_msg || "获取上传权限失败");
        }

        try {
            const authData = JSON.parse(uploadAuth.auth);
            const expireTime = new Date(authData.ExpiredTime).getTime();
            this.authExpireTime = expireTime;
            this.authCache = uploadAuth;

            console.log(`凭证有效期至: ${new Date(expireTime).toLocaleString()}`);
            return uploadAuth;
        } catch (error) {
            this.authExpireTime = now + 60 * 60 * 1000;
            this.authCache = uploadAuth;
            return uploadAuth;
        }
    }

    /**
     * 获取单张图片的上传地址
     */
    private async getUploadAddress(auth: UploadAuth): Promise<{
        host: string;
        storeUri: string;
        auth: string;
    }> {
        const authData = JSON.parse(auth.auth);
        const times = new Date();
        const amzDate = times
            .toISOString()
            .replace(/\.\d{3}Z$/, "Z")
            .replace(/[:-]/g, "");

        const region = "cn-north-1";
        const service = "imagex";

        const addrParamsConfig = {
            accessKeyId: authData.AccessKeyID,
            secretAccessKey: authData.SecretAccessKey,
            sessionToken: authData.SessionToken,
            region,
            service,
            method: "GET",
            pathname: "/",
            params: {
                Action: "ApplyImageUpload",
                ServiceId: "jm8ajry58r",
                Version: "2018-08-01",
                app_id: "2906",
                s: randomS(),
                user_id: "",
            },
        };

        const result = generateAuthorization(addrParamsConfig).authorization;
        const addrParamsQueryString = canonicalQueryString(addrParamsConfig.params);

        const response = await this.withRetry(
            () =>
                this.proxyHttp.api<ImageXResponse>(
                    {
                        method: "get",
                        url: `https://imagex.bytedanceapi.com/?${addrParamsQueryString}`,
                        headers: {
                            ...this.headers,
                            Authorization: result,
                            "Content-Type": "application/json",
                            "X-amz-date": amzDate,
                            "X-amz-security-token": authData.SessionToken,
                        },
                    },
                    { timeout: this.UPLOAD_TIMEOUT },
                ),
            3,
            1000,
            "获取上传地址",
        );

        if (!response) {
            throw new Error("获取上传地址失败: 响应为空");
        }

        if (response.ResponseMetadata?.Error) {
            throw new Error(
                `ImageX错误: ${response.ResponseMetadata.Error.Code} - ${response.ResponseMetadata.Error.Message}`,
            );
        }

        if (!response.Result) {
            console.error("ImageX 响应结构:", JSON.stringify(response, null, 2));
            throw new Error("获取上传地址失败: Result 为空");
        }

        if (!response.Result.UploadAddress) {
            console.error("ImageX Result 结构:", JSON.stringify(response.Result, null, 2));
            throw new Error("获取上传地址失败: UploadAddress 为空");
        }

        const uploadAddress = response.Result.UploadAddress;

        if (!uploadAddress.StoreInfos || uploadAddress.StoreInfos.length === 0) {
            throw new Error("获取上传地址失败: StoreInfos 为空");
        }

        if (!uploadAddress.UploadHosts || uploadAddress.UploadHosts.length === 0) {
            throw new Error("获取上传地址失败: UploadHosts 为空");
        }

        const storeInfo = uploadAddress.StoreInfos[0];

        console.log("获取上传地址成功:", {
            host: uploadAddress.UploadHosts[0],
            storeUri: storeInfo.StoreUri,
        });

        return {
            host: uploadAddress.UploadHosts[0],
            storeUri: storeInfo.StoreUri,
            auth: storeInfo.Auth,
        };
    }

    /**
     * 上传单张图片到TOS
     */
    private async uploadToTOS(
        imageInfo: ImageInfo,
        uploadAddr: { host: string; storeUri: string; auth: string },
    ): Promise<UploadResult> {
        const fileBuffer = fs.readFileSync(imageInfo.localPath);

        const uploadUrl = `https://${uploadAddr.host}/upload/v1/${uploadAddr.storeUri}`.trim();

        console.log(`上传至: ${uploadUrl.substring(0, 80)}...`);
        console.log(`文件大小: ${imageInfo.size} bytes, CRC32: ${imageInfo.md5}`);

        try {
            const uploadImgResp = await this.withRetry(
                () =>
                    this.proxyHttp.api(
                        {
                            method: "post",
                            url: uploadUrl,
                            headers: {
                                ...this.headers,
                                Authorization: uploadAddr.auth,
                                "Content-crc32": imageInfo.md5,
                                "Content-Type": "application/octet-stream",
                                "Content-Length": imageInfo.size.toString(),
                            },
                            data: fileBuffer,
                            httpAgent: this.uploadAgent,
                            httpsAgent: this.uploadAgent,
                            maxBodyLength: Infinity,
                            maxContentLength: Infinity,
                        },
                        { timeout: this.TOS_UPLOAD_TIMEOUT },
                    ),
                5,
                3000,
                "TOS上传",
            );

            console.log("TOS 上传响应:", uploadImgResp);

            const isSuccess =
                uploadImgResp &&
                (uploadImgResp.code === 2000 ||
                    uploadImgResp.status === 200 ||
                    uploadImgResp.statusCode === 200 ||
                    (uploadImgResp.data && uploadImgResp.data.crc32) ||
                    uploadImgResp.message === "Success");
            if (!isSuccess) {
                throw new Error(`上传失败: ${JSON.stringify(uploadImgResp)}`);
            }

            console.log(`上传成功: ${uploadAddr.storeUri}`);

            return {
                uri: uploadAddr.storeUri,
                width: imageInfo.width,
                height: imageInfo.height,
                size: imageInfo.size,
                md5: imageInfo.md5,
            };
        } catch (error: any) {
            console.error("TOS 上传错误:", error.message);
            if (error.response) {
                console.error("错误响应:", error.response.data);
            }
            throw error;
        }
    }

    /**
     * 处理单张图片（下载+上传）- 失败直接跳过
     */
    private async processSingleImage(imageUrl: string, index: number, total: number): Promise<UploadResult | null> {
        console.log(`\n[${index + 1}/${total}] 处理图片: ${imageUrl.substring(0, 50)}...`);

        const fileName = getFilenameFromUrl(imageUrl);
        const localPath = path.resolve(this.tmpCachePath, fileName);

        try {
            await downloadImage(imageUrl, localPath);
            console.log(`图片下载完成: ${localPath}`);

            const imageInfo = await this.getImageInfo(localPath);
            console.log(`图片信息: ${imageInfo.width}x${imageInfo.height}, ${imageInfo.size} bytes`);

            const auth = await this.getValidAuth();
            const uploadAddr = await this.getUploadAddress(auth);
            const result = await this.uploadToTOS(imageInfo, uploadAddr);

            this.safeDeleteFile(localPath);
            return result;
        } catch (error: any) {
            console.error(`[${index + 1}/${total}] 图片上传失败，已跳过: ${imageUrl}`, error?.message ?? error);
            this.safeDeleteFile(localPath);
            return null;
        }
    }

    /**
     * 获取图片信息
     */
    private async getImageInfo(localPath: string): Promise<ImageInfo> {
        const stats = fs.statSync(localPath);

        let width = 1080;
        let height = 1920;

        try {
            const buffer = fs.readFileSync(localPath);
            const dimensions = sizeOf(buffer);
            width = dimensions.width;
            height = dimensions.height;
        } catch (e) {
            // 使用默认值
        }

        return {
            localPath,
            width,
            height,
            size: stats.size,
            md5: calculateFileCrc32(localPath),
        };
    }

    /**
     * 批量上传图片（带并发控制）
     */
    async uploadImages(imageUrls: string[]): Promise<UploadResult[]> {
        const results: UploadResult[] = [];
        const total = imageUrls.length;

        console.log(`\n========== 开始批量上传 ${total} 张图片 ==========\n`);

        for (let i = 0; i < total; i += this.CONCURRENT_LIMIT) {
            const batch = imageUrls.slice(i, i + this.CONCURRENT_LIMIT);
            const batchIndex = Math.floor(i / this.CONCURRENT_LIMIT) + 1;
            const totalBatches = Math.ceil(total / this.CONCURRENT_LIMIT);

            console.log(`\n--- 批次 ${batchIndex}/${totalBatches} (${batch?.length} 张) ---`);

            const batchPromises = batch.map((url, idx) => {
                const globalIndex = i + idx;
                return this.processSingleImage(url, globalIndex, total);
            });

            const batchResults = await Promise.all(batchPromises);
            // 过滤掉失败的图片（null 值）
            const successResults = batchResults.filter((r): r is UploadResult => r !== null);
            results.push(...successResults);

            const failedCount = batchResults.length - successResults.length;
            if (failedCount > 0) {
                console.warn(`批次 ${batchIndex} 中有 ${failedCount} 张图片上传失败`);
            }

            if (i + this.CONCURRENT_LIMIT < total) {
                console.log(`等待 ${this.UPLOAD_DELAY}ms...`);
                await this.delay(this.UPLOAD_DELAY);
            }
        }

        console.log(`\n========== 全部 ${total} 张图片上传完成 ==========\n`);
        return results;
    }

    /**
     * 上传封面图（单张）
     */
    async uploadCover(coverUrl: string): Promise<UploadResult | null> {
        console.log("\n========== 上传封面 ==========\n");
        const result = await this.processSingleImage(coverUrl, 0, 1);

        if (!result) {
            console.error(`封面图上传失败，已跳过: ${coverUrl}`);
        }

        return result;
    }

    /**
     * 安全删除文件
     */
    private safeDeleteFile(filePath: string): void {
        try {
            if (fs.existsSync(filePath)) {
                fs.unlinkSync(filePath);
                console.log(`清理临时文件: ${path.basename(filePath)}`);
            }
        } catch (e) {
            // 忽略删除错误
        }
    }

    private delay(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    private async withRetry<T>(fn: () => Promise<T>, retries = 3, delayMs = 1000, label = ""): Promise<T> {
        for (let attempt = 1; attempt <= retries; attempt++) {
            try {
                return await fn();
            } catch (err: any) {
                if (attempt === retries) throw err;
                const waitTime = delayMs * attempt; // 递增延迟
                console.warn(`${label} 第 ${attempt} 次失败，${waitTime}ms 后重试:`, err?.message ?? err);
                await this.delay(waitTime);
            }
        }
        throw new Error("unreachable");
    }

    /**
     * 销毁连接池
     */
    destroy(): void {
        this.uploadAgent.destroy();
    }
}
